/**
 * DataTable — generic data table built on TanStack Table + shadcn Table primitives.
 *
 * The data layer (columns, sorting, filtering, pagination) is owned by TanStack.
 * This component is the UI shell: sticky header, loading skeletons, empty state,
 * row click navigation, and optional top/footer slots.
 *
 * Column definitions live in `columns.tsx` files co-located with each feature.
 * Source of truth for column API: @tanstack/react-table `ColumnDef<T>`.
 *
 * Supports two pagination modes:
 *   - Client-side: pass `getPaginationRowModel()` to useReactTable, use <DataTablePagination table={table} />
 *   - Server-side: pass `manualPagination` + `manualSorting`, use <DataTablePagination currentPage=... />
 *
 * @see apps/web-app/.claude/docs/data-table.md
 * @see https://ui.shadcn.com/docs/components/base/data-table
 */
'use client';

import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  type OnChangeFn,
  type PaginationState,
  type Row,
  type SortingState,
  type Table as TanStackTable,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';
import { Skeleton } from '@web-app/components/ui/Skeleton/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@web-app/components/ui/Table/table';
import { cn } from '@web-app/lib/utils';
import * as React from 'react';

interface DataTableProps<TData> {
  /** Column definitions — ColumnDef<TData>[] from @tanstack/react-table. */
  columns: ColumnDef<TData>[];
  /** Row data. */
  data: TData[];

  // ── Loading / empty states ──────────────────────────────────────────────
  /** Show skeleton loading rows instead of data. */
  loading?: boolean;
  /** Number of skeleton rows to render while loading. Default: 10. */
  loadingRowCount?: number;
  /** Content rendered when data is empty and not loading. Default: "No results." */
  emptyState?: React.ReactNode;

  // ── Sorting ────────────────────────────────────────────────────────────
  /** Controlled sorting state (for server-side sort). */
  sorting?: SortingState;
  /** Called when user clicks a sortable column header. */
  onSortingChange?: OnChangeFn<SortingState>;
  /**
   * Set true for server-side sorting (disables client-side sort logic).
   * Must also pass `sorting` + `onSortingChange`.
   */
  manualSorting?: boolean;

  // ── Pagination ─────────────────────────────────────────────────────────
  /** Controlled pagination state (for server-side pagination). */
  pagination?: PaginationState;
  /** Called when page/pageSize changes. */
  onPaginationChange?: OnChangeFn<PaginationState>;
  /**
   * Set true for server-side pagination (disables client-side page slicing).
   * Must also set `rowCount` for TanStack to compute page count.
   */
  manualPagination?: boolean;
  /**
   * Total row count (server-side). Required when `manualPagination` is true
   * so TanStack can compute `getCanNextPage()`.
   */
  rowCount?: number;

  // ── Global filter (client-side search) ────────────────────────────────
  /**
   * Controlled global filter value for client-side search.
   * When set, DataTable automatically enables `getFilteredRowModel`.
   */
  globalFilter?: string;
  onGlobalFilterChange?: OnChangeFn<string>;

  // ── Column visibility ──────────────────────────────────────────────────
  /** Controlled column visibility (use with DataTableViewOptions). */
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>;

  // ── Row interaction ────────────────────────────────────────────────────
  /**
   * Called when a data row is clicked. Receives the row's original data.
   * Applied to every <tr> except header/footer.
   */
  onRowClick?: (row: TData) => void;

  // ── Layout ─────────────────────────────────────────────────────────────
  /**
   * Make the table header sticky. Adds `sticky top-0 z-10` to <thead>.
   * The wrapping card must have `overflow-auto` for this to work.
   */
  stickyHeader?: boolean;

  // ── Render slots ───────────────────────────────────────────────────────
  /**
   * Slot rendered above the table (e.g. DataTableSearch + DataTableViewOptions).
   * Receives the TanStack `table` instance for advanced use (e.g. filter access).
   */
  renderTopBar?: (table: TanStackTable<TData>) => React.ReactNode;
  /**
   * Slot rendered below the table (e.g. DataTablePagination).
   * Receives the TanStack `table` instance.
   * If omitted no footer is rendered — pass your own DataTablePagination.
   */
  renderFooter?: (table: TanStackTable<TData>) => React.ReactNode;

  // ── TanStack passthrough ───────────────────────────────────────────────
  getRowId?: (row: TData) => string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  meta?: Record<string, any>;

  className?: string;
}


function DataTable<TData>({
  columns,
  data,
  loading = false,
  loadingRowCount = 10,
  emptyState = 'No results.',
  sorting,
  onSortingChange,
  manualSorting = false,
  pagination,
  onPaginationChange,
  manualPagination = false,
  rowCount,
  globalFilter,
  onGlobalFilterChange,
  columnVisibility,
  onColumnVisibilityChange,
  onRowClick,
  stickyHeader = false,
  renderTopBar,
  renderFooter,
  getRowId,
  meta,
  className,
}: DataTableProps<TData>) {
  const hasClientFilter = globalFilter !== undefined;
  // Enable client-side pagination when pagination state is controlled but
  // manualPagination is false (i.e. the caller wants DataTable to slice rows).
  const hasClientPagination = pagination !== undefined && !manualPagination;

  const table = useReactTable<TData>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    // Enable client-side filtering when globalFilter is provided.
    // TanStack's default globalFilterFn is `includesString` which calls
    // `.toString().toLowerCase().includes(search)` on each cell value —
    // works for both strings and numbers without any custom function.
    ...(hasClientFilter && { getFilteredRowModel: getFilteredRowModel() }),
    // Enable client-side pagination when pagination state is provided without manualPagination.
    ...(hasClientPagination && { getPaginationRowModel: getPaginationRowModel() }),
    // Controlled state (only include keys that are externally controlled)
    state: {
      ...(sorting !== undefined && { sorting }),
      ...(pagination !== undefined && { pagination }),
      ...(globalFilter !== undefined && { globalFilter }),
      ...(columnVisibility !== undefined && { columnVisibility }),
    },
    // Sorting
    ...(onSortingChange && { onSortingChange }),
    manualSorting,
    // Pagination
    ...(onPaginationChange && { onPaginationChange }),
    manualPagination,
    ...(rowCount !== undefined && { rowCount }),
    // Global filter
    ...(onGlobalFilterChange && { onGlobalFilterChange }),
    // Column visibility
    ...(onColumnVisibilityChange && { onColumnVisibilityChange }),
    // Misc
    ...(getRowId && { getRowId }),
    ...(meta && { meta }),
  });

  const columnCount = columns.length;

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <div className="overflow-hidden rounded-xl border border-border bg-card shadow-default-xs">
        {renderTopBar && (
          <div className="flex items-center gap-4 border-b border-border px-4 py-3">
            {renderTopBar(table)}
          </div>
        )}

        <Table>
          <TableHeader className={cn(stickyHeader && 'sticky top-0 z-10')}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent border-b border-border">
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {loading ? (
              <LoadingRows columnCount={columnCount} rowCount={loadingRowCount} />
            ) : table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <DataRow
                  key={row.id}
                  row={row}
                  onRowClick={onRowClick}
                />
              ))
            ) : (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={columnCount} className="h-32 text-center text-muted-foreground">
                  {emptyState}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {renderFooter?.(table)}
    </div>
  );
}

// ── Internal sub-components ─────────────────────────────────────────────────

function LoadingRows({
  columnCount,
  rowCount,
}: {
  columnCount: number;
  rowCount: number;
}) {
  return (
    <>
      {Array.from({ length: rowCount }, (_, rowIdx) => (
        <TableRow key={rowIdx} className="hover:bg-transparent">
          {Array.from({ length: columnCount }, (__, colIdx) => (
            <TableCell key={colIdx}>
              <Skeleton className="h-5 w-full max-w-[160px]" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

const INTERACTIVE_SELECTORS = 'a, button, input, select, textarea, [role="button"], [role="link"], [role="menuitem"], [role="option"]';

function DataRow<TData>({
  row,
  onRowClick,
}: {
  row: Row<TData>;
  onRowClick?: (original: TData) => void;
}) {
  function handleClick(e: React.MouseEvent<HTMLTableRowElement>) {
    if (!onRowClick) return;
    if ((e.target as Element).closest(INTERACTIVE_SELECTORS)) return;
    onRowClick(row.original);
  }

  return (
    <TableRow
      data-state={row.getIsSelected() ? 'selected' : undefined}
      onClick={onRowClick ? handleClick : undefined}
      className={cn(onRowClick && 'cursor-pointer')}
    >
      {row.getVisibleCells().map((cell) => (
        <TableCell key={cell.id}>
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </TableCell>
      ))}
    </TableRow>
  );
}

export { DataTable };
export type { DataTableProps };
